import { addChildAt } from '@ps/bridge/qc/node'
import store from '@ps/store'
import qc from 'qc'
import * as movieClipScript from '@ps/bridge/qc/scripts/movie-clip'
import { MovieClipScript } from '@ps/bridge/qc/scripts/movie-clip'
import { findByUuid } from '@ps/bridge/qc/node/utils'
import { Event, EventBus } from '@ps/utils/event-bus'
import { isEraseBrushImages } from '@ps/bridge/qc/base'

export type AddAtlasActionValue = {
  newValue: {
    node: qc.Node
    parent: qc.Node
    index: number
    realNode?: qc.Node
  }
  oldValue: {
    node: qc.Node
    activeUuid: string
  }
}

export const getAddSequenceAction = ({
  node,
  parent,
  index,
  activeUuid,
  realNode,
}: {
  node: qc.Node
  parent: qc.Node
  realNode: qc.Node
  index: number
  activeUuid: string
}) => {
  return {
    execute: async ({ newValue }: AddAtlasActionValue) => {
      const { node, parent, index, realNode } = newValue
      // 新增节点的时候在 recordHistory 之前已经增加节点了，所以需要判断节点树上是否有该节点
      if (!findByUuid(node.uuid)) {
        addChildAt({
          node,
          parent,
          index,
        })
      }
      // 新增节点后需要触发 Event.ADD_GAME_OBJECT 事件，有其他地方需要监听该事件。如需要读取该节点类型相关的事件
      EventBus.$emit(Event.ADD_GAME_OBJECT, node.uuid)
      if (!isEraseBrushImages(parent)) {
        await store.dispatch('workspace/game/updateActiveNode', {
          node: realNode || node,
          record: false,
        })
      }
    },
    undo: ({ oldValue }: AddAtlasActionValue) => {
      const { node, activeUuid } = oldValue
      node.removeSelf()
      // 如果undo的时候记录了要激活的节点，则激活该节点
      const activeNode = findByUuid(activeUuid)
      if (activeNode) {
        store.dispatch('workspace/game/updateActiveNode', {
          node: activeNode,
          record: false,
        })
      } else {
        store.dispatch('workspace/game/clearActiveNode', {
          record: false,
        })
      }
    },
    value: {
      newValue: {
        node,
        parent,
        index,
        realNode,
      },
      oldValue: {
        node,
        activeUuid,
      },
    },
    methodName: 'getAddSequenceAction',
  }
}

export type SequenceActionAttrs = {
  interval: number
  playTime: number
  autoPlay: boolean
  keepLastFrame: boolean
}
export type SetSequenceActionAttrsActionValue = {
  newValue: SequenceActionAttrs
  oldValue: SequenceActionAttrs
}
export const getSetSequenceActionAttrsAction = (
  value: SetSequenceActionAttrsActionValue,
) => {
  const update = (value: SequenceActionAttrs) => {
    const { interval, playTime, autoPlay, keepLastFrame } = value
    store.commit('workspace/sequenceManager/updateInterval', { interval })
    store.commit('workspace/sequenceManager/updatePlayTime', { playTime })
    store.commit('workspace/sequenceManager/updateAutoPlay', { autoPlay })
    store.commit('workspace/sequenceManager/updateKeepLastFrame', {
      keepLastFrame,
    })
    store.dispatch('workspace/sequenceManager/playAction')
  }
  return {
    execute: ({ newValue }: SetSequenceActionAttrsActionValue) => {
      update(newValue)
    },
    undo: ({ oldValue }: SetSequenceActionAttrsActionValue) => {
      update(oldValue)
    },
    value,
    methodName: 'getSetSequenceActionAttrsAction',
  }
}

export type UpdateTargetActionValue = {
  newValue: {
    actionName: string
    images: string
    node: qc.Node
    allImages?: string
  }[]
  oldValue: {
    actionName: string
    images: string
    node: qc.Node
    allImages?: string
  }[]
}
export const getUpdateTargetAction = (value: UpdateTargetActionValue) => {
  return {
    execute: async ({ newValue }: UpdateTargetActionValue) => {
      const taskList = newValue.map(async (item) => {
        const mv = movieClipScript.updateTargetActionBin(
          item,
        ) as MovieClipScript

        if (!mv) return
        const actionName = item.actionName
        store.commit('workspace/sequenceManager/updateData', {
          node: item.node,
          data: {
            ...store.state.workspace.sequenceManager.data,
            [actionName]: mv.data[actionName],
            allFramePlaySmart: mv.data.allFramePlaySmart,
          },
        })
        store.commit('workspace/sequenceManager/updateActionName', {
          node: item.node,
          actionName,
        })
        // @ts-ignore
        mv.gotoAndPlay(actionName, mv.playTime, null, null, mv.pingpong)
      })
      await Promise.all(taskList)

      // 刷新激活节点样式
      newValue.forEach((item) => {
        store.dispatch(
          'workspace/game/sequenceManager/playSequenceFrame',
          item.node,
        )
      })
    },
    undo: async ({ oldValue }: UpdateTargetActionValue) => {
      const taskList = oldValue.map(async (item) => {
        const mv = movieClipScript.updateTargetActionBin(
          item,
        ) as MovieClipScript
        if (!mv) return
        const actionName = item.actionName
        movieClipScript.updateTargetActionBin(item)
        // @ts-ignore
        mv.gotoAndPlay(actionName, mv.playTime, null, null, mv.pingpong)
      })
      await Promise.all(taskList)
      // 刷新激活节点样式
      oldValue.forEach((item) => {
        store.dispatch(
          'workspace/game/sequenceManager/playSequenceFrame',
          item.node,
        )
      })
    },
    value,
    methodName: 'getUpdateTargetAction',
  }
}
